import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Facebook, Linkedin, Twitter, Instagram, Youtube, Globe, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { COMPANY, FOOTER_GROUPS } from "../../data/site";

const SOCIAL_ICONS = { facebook: Facebook, linkedin: Linkedin, twitter: Twitter, instagram: Instagram, youtube: Youtube };

export const Footer = () => {
  const [socials, setSocials] = useState([]);
  useEffect(() => {
    api.get("/brand").then(({ data }) => setSocials(Array.isArray(data.social_links) ? data.social_links : [])).catch(() => {});
  }, []);
  return (
    <footer className="bg-navy text-white" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src={COMPANY.logo} alt={COMPANY.name} className="h-14 w-14 rounded-full object-cover ring-1 ring-white/15" />
              <div>
                <span className="block font-serif text-xl">Azimi &amp; Co.</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-gold-light">Vision Recruitment</span>
              </div>
            </div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/65" data-testid="footer-description">
              Specialist healthcare recruitment connecting trusted employers with exceptional
              clinical and non-clinical professionals.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/employers#request" className="inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gold-dark" data-testid="footer-request-talent">
                Request Talent <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/candidates#submit" className="inline-flex items-center gap-2 rounded-lg border border-white/25 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="footer-submit-cv">
                Submit Your CV <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <ul className="mt-6 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 text-gold" /><a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></li>
              <li className="flex items-start gap-3"><Phone className="mt-0.5 h-4 w-4 text-gold" /><span className="flex flex-col"><a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="hover:text-white">{COMPANY.phone}</a><a href={`tel:${COMPANY.phoneUk.replace(/\s/g, "")}`} className="hover:text-white">{COMPANY.phoneUk}</a></span></li>
              <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-gold" /><span>{COMPANY.address}</span></li>
              <li className="flex items-start gap-3"><Clock className="mt-0.5 h-4 w-4 text-gold" /><span>{COMPANY.hours}</span></li>
            </ul>
            {socials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2.5" data-testid="footer-socials">
                {socials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.key] || Globe;
                  return (
                    <a
                      key={s.key}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={s.label}
                      title={s.label}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 text-white/75 transition-colors hover:border-gold hover:text-gold"
                      data-testid={`footer-social-${s.key}`}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-8">
            {FOOTER_GROUPS.map((g) => (
              <div key={g.title}>
                <h4 className="font-serif text-base text-white">{g.title}</h4>
                <ul className="mt-4 space-y-2.5 text-sm text-white/65">
                  {g.links.map((l) => (
                    <li key={l.label}><Link to={l.to} className="hover:text-gold">{l.label}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
            <div>
              <h4 className="font-serif text-base text-white">Legal</h4>
              <ul className="mt-4 space-y-2.5 text-sm text-white/65">
                <li><Link to="/privacy" className="hover:text-gold" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-gold" data-testid="footer-terms-link">Terms of Business</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-gold" data-testid="footer-cookie-policy-link">Cookie Policy</Link></li>
                <li><Link to="/accessibility" className="hover:text-gold" data-testid="footer-accessibility-link">Accessibility</Link></li>
                <li><Link to="/speak-up" className="hover:text-gold" data-testid="footer-speak-up-link">Speak Up</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-sm text-white/50 sm:flex-row sm:items-center">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
            <p>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
          </div>
          <p className="max-w-md text-xs text-white/40" data-testid="footer-tagline">
            {COMPANY.tagline}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
